import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Stack, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import RecordProducer, { RecordProducerFormState } from "modules/record/record-producer/components/RecordProducer";
import {
    ConfirmActionDialog,
    FlussPageHeader,
    FlussStatusChip,
} from "modules/fluss/components/design-kit/FlussDesignKit";
import { useFlussCable } from "modules/fluss/hooks/useFlussCable";
import { flussApi, flussApiError } from "modules/fluss/services/fluss-api";
import { flussDefinitionLabel } from "modules/fluss/utils/flussDefinitionLabel";
import { resolveDisplayText } from "modules/record/shared/services/recordMetadataLabels";
import { FlussAnchor } from "modules/fluss/types/fluss";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { formatConditionSummary, resolveConditionColumns } from "modules/record/record-list/services/conditionSummary";
import { useChoiceLabelMaps } from "modules/record/shared/hooks/useChoiceOptions";
import { recordMutationBus } from "shared/services/recordMutationBus";
import useNotification from "component/global/event-handler/hooks/use-notification";
import { useSession } from "app/hooks/useSession";
import { canOperateFluss } from "modules/fluss/utils/permissions";
import { clampTaskMainWidth, loadTaskMainWidth, saveTaskMainWidth } from "./fluss-task-layout";

// PRIM-026 (round 8, Bloco B): the same route the fixed dispatch_form's form_url points at
// (package 4.66.1) -- update mode opens filled in with the process' own anchor record, the
// same mechanism 4.64.1 wired into sp_form_viewer's recordContext.
const formUrl = (formId: string, formMode: string | null | undefined, anchor: FlussAnchor | null | undefined) => {
    const recordId = formMode === "update" ? anchor?.record_id : null;
    return `/g/service-portal/form/${encodeURIComponent(formId)}?embed=1${recordId ? `&recordId=${encodeURIComponent(recordId)}` : ""}`;
};

const isApprovalTask = (task: { node_type?: string | null } | undefined) =>
    task?.node_type === "approval";

const FlussTaskPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
    const { taskId = "" } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const notification = useNotification();
    const { data: user } = useSession();
    const { data: allColumns = [] } = useColumns();
    const { data: tables = [] } = useTables();
    const [outcome, setOutcome] = useState<string | null>(null);
    const [dirtyOutcome, setDirtyOutcome] = useState<string | null>(null);
    const [savingForms, setSavingForms] = useState(false);
    const [saveError, setSaveError] = useState("");
    const formStates = useRef<Record<string, RecordProducerFormState>>({});
    const [dirtySlots, setDirtySlots] = useState<string[]>([]);
    const updateFormState = useCallback((slot: string, state: RecordProducerFormState | null) => {
        if (state) formStates.current[slot] = state;
        else delete formStates.current[slot];
        const next = Object.keys(formStates.current).filter((key) => formStates.current[key].isDirty).sort();
        setDirtySlots((previous) => previous.join("|") === next.join("|") ? previous : next);
    }, []);
    const onWorkFormState = useCallback((state: RecordProducerFormState | null) => updateFormState("work", state), [updateFormState]);
    const onAnchorFormState = useCallback((state: RecordProducerFormState | null) => updateFormState("anchor", state), [updateFormState]);
    const [comment, setComment] = useState("");
    const [mainWidth, setMainWidth] = useState(() => loadTaskMainWidth(user?.id));
    const [sideCollapsed, setSideCollapsed] = useState(false);
    const [sideTab, setSideTab] = useState<"associated" | "context">("context");
    const splitRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => setMainWidth(loadTaskMainWidth(user?.id)), [user?.id]);
    const task = useQuery({
        queryKey: ["fluss", "tasks", taskId],
        queryFn: () => flussApi.getTask(taskId),
        enabled: Boolean(taskId),
    });
    const complete = useMutation({
        mutationFn: (selected: string) => isApprovalTask(task.data)
            ? flussApi.completeTask(taskId, selected, { comment: comment.trim() })
            : flussApi.completeTask(taskId, selected),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ["fluss", "tasks"], exact: true, refetchType: "all" });
            navigate("/home/fluss/inbox");
        },
    });

    const beginOutcome = (value: string) => {
        setComment("");
        setSaveError("");
        if (dirtySlots.length) setDirtyOutcome(value);
        else setOutcome(value);
    };

    const resolveDirtyOutcome = async (save: boolean) => {
        if (!dirtyOutcome) return;
        const selected = dirtyOutcome;
        setSavingForms(true);
        setSaveError("");
        try {
            for (const slot of dirtySlots) {
                const state = formStates.current[slot];
                if (!state?.isDirty) continue;
                if (save && !(await state.save())) {
                    setSaveError(String(t("fluss.task.save_failed", { defaultValue: "Não foi possível salvar as alterações. Corrija o formulário e tente novamente." })));
                    return;
                }
                if (!save) state.discard();
            }
            setDirtyOutcome(null);
            if (save && task.data?.completion_condition) {
                const latest = await flussApi.getTask(taskId);
                if (latest.status === "completed") {
                    navigate("/home/fluss/inbox");
                    return;
                }
            }
            complete.mutate(selected);
        } catch (error) {
            setSaveError(flussApiError(error).message);
        } finally {
            setSavingForms(false);
        }
    };

    const recordTarget = task.data?.work_record;
    const anchor = task.data?.anchor;
    const workRecord = task.data?.work_record;
    const bodyUsesAnchor = !isApprovalTask(task.data) && !task.data?.form_id &&
        task.data?.node_type !== "manual_task" && !workRecord && Boolean(anchor);
    const showAssociated = Boolean(anchor && !bodyUsesAnchor &&
        (!workRecord || anchor.table !== workRecord.table || anchor.record_id !== workRecord.record_id));
    useEffect(() => {
        setSideTab(showAssociated ? "associated" : "context");
    }, [taskId, showAssociated]);

    const startResize = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (!splitRef.current) return;
        event.preventDefault();
        const container = splitRef.current;
        const move = (pointer: PointerEvent) => {
            const bounds = container.getBoundingClientRect();
            if (!bounds.width) return;
            const width = clampTaskMainWidth(((pointer.clientX - bounds.left) / bounds.width) * 100);
            setMainWidth(width);
        };
        const stop = (pointer: PointerEvent) => {
            const bounds = container.getBoundingClientRect();
            if (bounds.width) {
                const width = clampTaskMainWidth(((pointer.clientX - bounds.left) / bounds.width) * 100);
                setMainWidth(width);
                saveTaskMainWidth(width, user?.id);
            }
            window.removeEventListener("pointermove", move);
            window.removeEventListener("pointerup", stop);
        };
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", stop, { once: true });
    }, [user?.id]);
    const conditionColumns = useMemo(() => {
        const table = tables.find((candidate) => candidate.name === recordTarget?.table);
        return allColumns.filter((column) => column.sys_table_id === table?.id);
    }, [allColumns, tables, recordTarget?.table]);
    const choiceLabelMaps = useChoiceLabelMaps(conditionColumns);
    const conditionSummary = useMemo(() => {
        if (!task.data?.completion_condition) return "";
        const dynamicColumns = conditionColumns.flatMap((column) =>
            resolveConditionColumns(column, {}, conditionColumns, allColumns, tables));
        return formatConditionSummary(task.data.completion_condition, conditionColumns, t, choiceLabelMaps, tables, [...allColumns, ...dynamicColumns]);
    }, [task.data?.completion_condition, conditionColumns, choiceLabelMaps, allColumns, tables, t]);

    useEffect(() => {
        if (!recordTarget || !task.data?.completion_condition) return;
        let checking = false;
        return recordMutationBus.subscribe((event) => {
            if (checking || event.table !== recordTarget.table || (event.ids?.length && !event.ids.includes(recordTarget.record_id))) return;
            checking = true;
            void flussApi.getTask(taskId).then(async (latest) => {
                queryClient.setQueryData(["fluss", "tasks", taskId], latest);
                if (latest.status === "completed") {
                    notification.success("FLUSS-TASK-COMPLETED", String(t("fluss.task.completed_after_save", { defaultValue: "Tarefa concluída após salvar o registro." })));
                    await queryClient.invalidateQueries({ queryKey: ["fluss", "tasks"], exact: true, refetchType: "all" });
                    navigate("/home/fluss/inbox");
                }
            }).finally(() => { checking = false; });
        });
    }, [recordTarget?.table, recordTarget?.record_id, task.data?.completion_condition, taskId, queryClient, navigate, notification, t]);

    useFlussCable({
        instanceId: task.data?.instance.id,
        onReceived: () => queryClient.invalidateQueries({ queryKey: ["fluss", "tasks", taskId] }),
    });

    if (!task.data) {
        return <Box className="fluss-page" />;
    }

    const isApproval = isApprovalTask(task.data);
    const commentRequired = outcome === "reject" || outcome === "more_info";
    const error = complete.error ? flussApiError(complete.error) : null;
    const actionTitles: Record<string, string> = {
        approve: String(t("fluss.task.confirm_approve", { defaultValue: "Aprovar tarefa" })),
        reject: String(t("fluss.task.confirm_reject", { defaultValue: "Rejeitar tarefa" })),
        more_info: String(t("fluss.task.confirm_more_info", { defaultValue: "Solicitar mais informações" })),
    };

    return (
        <Box className="fluss-page">
            <Box ref={splitRef} data-testid="fluss-task-split" sx={{ display: "grid", minWidth: 0,
                gridTemplateColumns: { xs: "minmax(0, 1fr)", md: sideCollapsed ? "minmax(0, 1fr)" : `minmax(0, ${mainWidth}fr) 10px minmax(0, ${100 - mainWidth}fr)` },
                gap: { xs: 2, md: 0 }, alignItems: "start" }}>
            <Stack gap={2} data-testid="fluss-task-main" sx={{ minWidth: 0, pr: { md: sideCollapsed ? 0 : 1 } }}>
                <FlussPageHeader
                    title={task.data.title}
                    subtitle={flussDefinitionLabel(t, task.data.definition_name)}
                    status={<FlussStatusChip status={task.data.status} />}
                    actions={
                        <>
                            <Button variant="text" onClick={() => setSideCollapsed((value) => !value)} sx={{ display: { xs: "none", md: "inline-flex" } }}>
                                {sideCollapsed ? t("fluss.task.show_context", { defaultValue: "Mostrar contexto" }) : t("fluss.task.hide_context", { defaultValue: "Recolher contexto" })}
                            </Button>
                            {task.data.allowed_outcomes.map((value) => (
                                <Button key={value} variant="contained" onClick={() => beginOutcome(value)}>
                                    {t(`fluss.outcome.${value}`, { defaultValue: value })}
                                </Button>
                            ))}
                        </>
                    }
                />
                {error && <Alert severity="error">{error.message}</Alert>}
                {task.data.completion_condition && conditionSummary && <Alert severity="info">
                    {t("fluss.task.completion_condition", { summary: conditionSummary, defaultValue: "Esta tarefa é concluída quando o registro atender a: {{summary}}" })}
                </Alert>}
                {!isApproval && task.data.instructions?.trim() && <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline">{t("fluss.task.manual_instructions", { defaultValue: "Instructions" })}</Typography>
                    <Typography whiteSpace="pre-wrap">{resolveDisplayText(t, task.data.instructions)}</Typography>
                </Paper>}
                {isApproval ? <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6">{t("fluss.task.approval_decision", { defaultValue: "Decisão da aprovação" })}</Typography>
                    <Typography whiteSpace="pre-wrap">{task.data.description?.trim() || task.data.instructions?.trim()
                        ? resolveDisplayText(t, task.data.description?.trim() || task.data.instructions || "")
                        : t("fluss.task.approval_hint", { defaultValue: "Analise o registro ao lado e escolha um desfecho nos botões acima." })}</Typography>
                    <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{t("fluss.task.available_outcomes", { defaultValue: "Desfechos disponíveis" })}</Typography>
                    <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                        {task.data.allowed_outcomes.map((value) => <Typography component="li" key={value}>
                            {t(`fluss.outcome.${value}`, { defaultValue: value })}
                        </Typography>)}
                    </Box>
                </Paper> : task.data.node_type === "manual_task" && task.data.description?.trim() ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack gap={0.75}>
                            <Typography variant="overline">
                                {t("fluss.task.manual_instructions", { defaultValue: "Instructions" })}
                            </Typography>
                            <Typography whiteSpace="pre-wrap">
                                {resolveDisplayText(t, task.data.description || "")}
                            </Typography>
                        </Stack>
                    </Paper>
                ) : task.data.form_id ? (
                    <Paper variant="outlined" sx={{ height: "70vh", overflow: "hidden" }}>
                        <iframe
                            title={String(t("fluss.task.form_frame_title", { defaultValue: "Task form" }))}
                            src={formUrl(task.data.form_id, task.data.form_mode, anchor)}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    </Paper>
                ) : workRecord || bodyUsesAnchor ? (
                    <RecordProducer tableName={(workRecord || anchor)!.table} sysId={(workRecord || anchor)!.record_id}
                        explainTaskAccess embedded showFullRecordLink={false} onFormStateChange={onWorkFormState} />
                ) : (
                    <Alert severity="info">
                        {t("fluss.task.no_anchor", { defaultValue: "This task has no linked business record." })}
                    </Alert>
                )}
            </Stack>
            {isMdUp && <Box role="separator" aria-orientation="vertical" aria-label={String(t("fluss.task.resize_columns", { defaultValue: "Redimensionar colunas" }))}
                onPointerDown={startResize} sx={{ display: { xs: "none", md: sideCollapsed ? "none" : "flex" }, alignSelf: "stretch", cursor: "col-resize", alignItems: "flex-start", justifyContent: "center", touchAction: "none", '&::before': { content: '""', width: "2px", height: "100%", bgcolor: "divider" } }} />
            }
            <Paper variant="outlined" data-testid="fluss-task-sidebar" sx={{ display: { xs: "block", md: sideCollapsed ? "none" : "block" }, minWidth: 0,
                "--record-action-sticky-top": "48px",
                height: { md: "calc(100vh - 150px)" }, overflowY: "auto", position: "relative" }}>
                <Box sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1, borderBottom: 1, borderColor: "divider" }}>
                    <Tabs value={sideTab} onChange={(_, value) => setSideTab(value)} aria-label={String(t("fluss.task.context_tabs", { defaultValue: "Contexto da tarefa" }))} variant="fullWidth">
                        {showAssociated && <Tab value="associated" label={t("fluss.task.associated_record", { defaultValue: "Registro associado" })} />}
                        <Tab value="context" label={t("fluss.task.context_tab", { defaultValue: "Contexto" })} />
                    </Tabs>
                </Box>
                {showAssociated && anchor && <Box sx={{ p: 2, display: sideTab === "associated" ? "block" : "none" }}>
                    <RecordProducer tableName={anchor.table} sysId={anchor.record_id} embedded onFormStateChange={onAnchorFormState} />
                </Box>}
                <Box sx={{ p: 2, display: sideTab === "context" ? "block" : "none" }}>
                    <Stack gap={1.5}>
                        <Typography>{t("fluss.task.requester", { name: task.data.requester?.name || "—", defaultValue: "Requested by {{name}}" })}</Typography>
                        <Typography>{t("fluss.task.process_label", { defaultValue: "Processo" })}: {flussDefinitionLabel(t, task.data.definition_name)}</Typography>
                        {canOperateFluss(user) && <Link component={RouterLink} to={`/home/fluss/instances/${encodeURIComponent(task.data.instance.id)}`}>
                            {t("fluss.task.open_instance", { defaultValue: "Abrir instância no monitor" })}
                        </Link>}
                        {Boolean(task.data.record_summary?.length) && <Stack component="dl" gap={1}>
                            {(task.data.record_summary || []).map((field, index) => <Box key={`${field.label}-${index}`}>
                                <Typography component="dt" variant="body2" color="text.secondary">{field.label}</Typography>
                                <Typography component="dd" sx={{ m: 0 }}>{field.value}</Typography>
                            </Box>)}
                        </Stack>}
                    </Stack>
                </Box>
            </Paper>
            </Box>
            <ConfirmActionDialog
                open={Boolean(outcome)}
                title={task.data.node_type === "manual_task"
                    ? t("fluss.task.confirm_title", { defaultValue: "Concluir tarefa" })
                    : actionTitles[outcome || ""] || t("fluss.task.confirm_action", { action: t(`fluss.outcome.${outcome}`, { defaultValue: outcome || "" }), defaultValue: "Confirmar {{action}}" })}
                description={t("fluss.task.confirm_description", { defaultValue: "This action advances the process and cannot be undone." })}
                confirmLabel={t("fluss.common.confirm", { defaultValue: "Confirm" })}
                cancelLabel={t("fluss.common.cancel", { defaultValue: "Cancel" })}
                busy={complete.isPending}
                confirmDisabled={isApproval && commentRequired && !comment.trim()}
                onClose={() => setOutcome(null)}
                onConfirm={() => outcome && complete.mutate(outcome)}
            >
                {isApproval && <TextField
                    label={String(t("fluss.task.comment", { defaultValue: "Comment" }))}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    required={commentRequired}
                    fullWidth multiline minRows={2} sx={{ mt: 2 }}
                />}
            </ConfirmActionDialog>
            <Dialog open={Boolean(dirtyOutcome)} onClose={() => !savingForms && setDirtyOutcome(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{t("fluss.task.unsaved_title", { defaultValue: "Alterações não salvas" })}</DialogTitle>
                <DialogContent>
                    <Typography>{t("fluss.task.unsaved_description", {
                        records: dirtySlots.map((slot) => slot === "anchor" ? anchor?.display || anchor?.table || "—" : workRecord?.table || anchor?.table || "—").join(", "),
                        defaultValue: "Há alterações não salvas em {{records}}. O que deseja fazer?",
                    })}</Typography>
                    {saveError && <Alert severity="error" sx={{ mt: 2 }}>{saveError}</Alert>}
                    {isApproval && <TextField label={String(t("fluss.task.comment", { defaultValue: "Comment" }))} value={comment}
                        onChange={(event) => setComment(event.target.value)} required={dirtyOutcome === "reject" || dirtyOutcome === "more_info"}
                        fullWidth multiline minRows={2} sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDirtyOutcome(null)} disabled={savingForms}>{t("fluss.task.back", { defaultValue: "Voltar" })}</Button>
                    <Button onClick={() => void resolveDirtyOutcome(false)} disabled={savingForms || (isApproval && (dirtyOutcome === "reject" || dirtyOutcome === "more_info") && !comment.trim())}>
                        {t("fluss.task.discard_and_complete", { defaultValue: "Descartar e concluir" })}
                    </Button>
                    <Button variant="contained" onClick={() => void resolveDirtyOutcome(true)} disabled={savingForms || (isApproval && (dirtyOutcome === "reject" || dirtyOutcome === "more_info") && !comment.trim())}>
                        {t("fluss.task.save_and_complete", { defaultValue: "Salvar e concluir" })}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FlussTaskPage;
